import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext(null);

const DEFAULTS = {
  nombre_comercial: "DentCloud",
  eslogan: "",
  nit: "",
  direccion: "",
  telefono: "",
  email_contacto: "",
  color_primario: "#1B4332",
  color_secundario: "#52796F",
  color_fondo_light: "#FAF8F4",
  color_fondo_dark: "#0F1410",
  color_exito: "#16A34A",
  color_advertencia: "#F59E0B",
  color_error: "#DC2626",
  color_texto_principal: "#1A1C1A",
  color_texto_secundario: "#6E736E",
  fuente_titulos: "Inter",
  fuente_cuerpo: "Inter",
  tamano_base: "normal",
  border_radius: "subtle",
  sombras_estilo: "subtle",
  densidad: "normal",
  animaciones_activas: true,
  plantilla_aplicada: "clinico",
  mensaje_bienvenida: "",
  footer_recibos: "",
  logo_principal_b64: "",
  logo_cuadrado_b64: "",
  logo_documentos_b64: "",
};

// Convertir #RRGGBB a "R G B" (formato tailwind hsl-friendly via rgb).
function hexToRgbString(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "0 0 0";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}
// Convertir #RRGGBB a HSL "h s% l%" (formato Tailwind/shadcn).
function hexToHsl(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "0 0% 0%";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let H = 0, S = 0;
  if (max !== min) {
    const d = max - min;
    S = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: H = (g - b) / d + (g < b ? 6 : 0); break;
      case g: H = (b - r) / d + 2; break;
      case b: H = (r - g) / d + 4; break;
      default: break;
    }
    H *= 60;
  }
  return `${Math.round(H)} ${Math.round(S * 100)}% ${Math.round(l * 100)}%`;
}

const RADIUS_MAP = { none: "0px", subtle: "4px", rounded: "8px", extra: "16px" };
const FONT_SIZE_MAP = { compact: "14px", normal: "16px", comfort: "18px" };
const DENSITY_MAP = { compact: "0.75", normal: "1", spacious: "1.25" };

// Lista de fuentes Google soportadas (dinámicas)
const GOOGLE_FONTS = [
  "Inter", "Playfair Display", "Montserrat", "Poppins", "Lora",
  "DM Serif Display", "Space Grotesk", "Roboto", "Open Sans",
  "Lato", "Source Sans 3", "Nunito", "DM Sans",
];

function loadGoogleFonts(fontList) {
  // Eliminar link previo de branding si existe
  const prev = document.getElementById("branding-fonts");
  if (prev) prev.remove();
  const families = Array.from(new Set(fontList.filter((f) => GOOGLE_FONTS.includes(f))));
  if (families.length === 0) return;
  const link = document.createElement("link");
  link.id = "branding-fonts";
  link.rel = "stylesheet";
  const q = families.map((f) => `family=${encodeURIComponent(f)}:wght@400;500;600;700;800;900`).join("&");
  link.href = `https://fonts.googleapis.com/css2?${q}&display=swap`;
  document.head.appendChild(link);
}

function applyBrandingToDom(b, isDark) {
  const root = document.documentElement;
  // CSS vars custom (--brand-*)
  root.style.setProperty("--brand-primary", b.color_primario);
  root.style.setProperty("--brand-secondary", b.color_secundario);
  root.style.setProperty("--brand-bg-light", b.color_fondo_light);
  root.style.setProperty("--brand-bg-dark", b.color_fondo_dark);
  root.style.setProperty("--brand-success", b.color_exito);
  root.style.setProperty("--brand-warning", b.color_advertencia);
  root.style.setProperty("--brand-error", b.color_error);
  root.style.setProperty("--brand-text", b.color_texto_principal);
  root.style.setProperty("--brand-text-muted", b.color_texto_secundario);
  root.style.setProperty("--brand-primary-rgb", hexToRgbString(b.color_primario));
  // CSS vars compatibles con Tailwind/shadcn (hsl puro)
  root.style.setProperty("--primary", hexToHsl(b.color_primario));
  root.style.setProperty("--accent", hexToHsl(b.color_secundario));
  root.style.setProperty("--background", hexToHsl(isDark ? b.color_fondo_dark : b.color_fondo_light));
  root.style.setProperty("--foreground", hexToHsl(b.color_texto_principal));
  // Tipografía
  root.style.setProperty("--font-titles", `'${b.fuente_titulos}', sans-serif`);
  root.style.setProperty("--font-body", `'${b.fuente_cuerpo}', sans-serif`);
  root.style.setProperty("--brand-font-size", FONT_SIZE_MAP[b.tamano_base] || "16px");
  // Estilo
  root.style.setProperty("--brand-radius", RADIUS_MAP[b.border_radius] || "4px");
  root.style.setProperty("--brand-density", DENSITY_MAP[b.densidad] || "1");
  // Animaciones
  if (b.animaciones_activas === false) {
    root.style.setProperty("--brand-motion", "0");
    root.classList.add("motion-reduce");
  } else {
    root.style.setProperty("--brand-motion", "1");
    root.classList.remove("motion-reduce");
  }
  // Aplicar fuentes a body
  document.body.style.fontFamily = `var(--font-body)`;
  document.body.style.fontSize = FONT_SIZE_MAP[b.tamano_base] || "16px";
  // Cargar Google Fonts
  loadGoogleFonts([b.fuente_titulos, b.fuente_cuerpo]);
  // Title + favicon
  const baseTitle = b.nombre_comercial || "DentCloud";
  document.title = `${baseTitle} · Gestión odontológica`;

  // Mantener referencia al favicon original (provisto por la plantilla index.html)
  // para poder restaurarlo cuando un tenant no tenga logo cuadrado configurado.
  if (!window.__defaultFaviconHref) {
    const initial = document.querySelector("link[rel='icon']");
    window.__defaultFaviconHref = (initial && initial.getAttribute("href")) || "/favicon.ico";
  }

  let icon = document.querySelector("link[rel='icon']");
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.appendChild(icon);
  }
  // SIEMPRE actualizar: si hay logo del tenant lo usamos, sino restauramos el original.
  // Esto evita que el favicon de un tenant anterior se quede "pegado" al cambiar de
  // sesión, hacer logout, o navegar al login raíz.
  icon.href = b.logo_cuadrado_b64 || window.__defaultFaviconHref;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));

  // Watch for theme changes
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const { data } = await api.get("/branding");
      setBranding({ ...DEFAULTS, ...data });
    } catch {
      // fallback a defaults o branding público si no hay sesión
    } finally {
      setLoaded(true);
    }
  }, []);

  const fetchPublicBranding = useCallback(async (slug) => {
    try {
      const { data } = await api.get(`/branding/public/${slug}`);
      setBranding((b) => ({ ...b, ...data }));
    } catch { /* noop */ }
  }, []);

  // Aplicar al DOM cuando cambia branding o theme
  useEffect(() => {
    applyBrandingToDom(branding, isDark);
  }, [branding, isDark]);

  // Re-fetch al login (cuando aparece token)
  useEffect(() => {
    const token = localStorage.getItem("df_token");
    if (token) fetchBranding();
    // listener para cambios en localStorage (login/logout en otra pestaña)
    const onStorage = (e) => {
      if (e.key === "df_token") {
        if (e.newValue) fetchBranding();
        else setBranding(DEFAULTS);
      }
    };
    // listener para logout en la misma pestaña (custom event de AuthContext)
    const onLogout = () => setBranding(DEFAULTS);
    window.addEventListener("storage", onStorage);
    window.addEventListener("dc:logout", onLogout);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("dc:logout", onLogout);
    };
  }, [fetchBranding]);

  const updateBranding = useCallback(async (payload) => {
    const { data } = await api.put("/branding", payload);
    setBranding({ ...DEFAULTS, ...data });
    return data;
  }, []);

  const applyTemplate = useCallback(async (template) => {
    const { data } = await api.post("/branding/apply-template", { template });
    setBranding({ ...DEFAULTS, ...data });
    return data;
  }, []);

  const resetBranding = useCallback(async () => {
    const { data } = await api.post("/branding/reset");
    setBranding({ ...DEFAULTS, ...data });
    return data;
  }, []);

  const uploadLogo = useCallback(async (slot, dataUrl) => {
    const { data } = await api.post("/branding/logo", { slot, data_url: dataUrl });
    setBranding({ ...DEFAULTS, ...data });
    return data;
  }, []);

  const removeLogo = useCallback(async (slot) => {
    const { data } = await api.delete(`/branding/logo/${slot}`);
    setBranding({ ...DEFAULTS, ...data });
    return data;
  }, []);

  return (
    <BrandingContext.Provider value={{
      branding, loaded,
      refresh: fetchBranding,
      loadPublic: fetchPublicBranding,
      updateBranding, applyTemplate, resetBranding, uploadLogo, removeLogo,
      GOOGLE_FONTS,
    }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
